"use dom";

import "./editor.css";

import {
  EditorContent,
  JSONContent,
  useEditor,
  UseEditorOptions,
} from "@tiptap/react";

import StarterKit from "@tiptap/starter-kit";
import UnderlineExt from "@tiptap/extension-underline";
import LinkExt from "@tiptap/extension-link";
import ColorExt from "@tiptap/extension-color";
import HighlightExt from "@tiptap/extension-highlight";
import Placeholder from "@tiptap/extension-placeholder";
import TextStyle from "@tiptap/extension-text-style";
import {
  Ref,
  RefObject,
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
} from "react";
import {
  DOMImperativeFactory,
  DOMProps,
  useDOMImperativeHandle,
} from "expo/dom";

import * as Sentry from "@sentry/react-native";
import { useFonts } from "expo-font";
const extensions = ({ placeholder }: { placeholder?: string }) => [
  StarterKit,
  UnderlineExt,
  LinkExt,
  ColorExt,
  HighlightExt,
  TextStyle,
  Placeholder.configure({
    placeholder,
  }),
];

export interface RTRef {
  setContent: (content: JSONContent) => void;
}

export type RTProps = Pick<
  UseEditorOptions,
  "editable" | "content" | "onUpdate"
> & {
  ref: Ref<RTRef>;
  placeholder?: string;
  onSizeChange?: (height: number) => Promise<void>;
  dom?: DOMProps;
  fontMap: Record<string, string>;
  scaleFactor: number;
};

export default function RT(props: RTProps) {
  const editor = useEditor({
    extensions: extensions({ placeholder: props.placeholder }),
    editable: props.editable,
    content: props.content,
    onUpdate: props.onUpdate,

    onContentError(props) {
      Sentry.captureException(props.error);
    },
  });
  const ref = useRef<HTMLDivElement>(null);

  const onSize = props.onSizeChange;

  useFonts(props.fontMap);
  useDOMImperativeHandle(
    props.ref as unknown as Ref<DOMImperativeFactory>,
    () =>
      ({
        setContent(content?: JSONContent) {
          if (content) {
            editor?.commands.setContent(content);
          }
        },
      }) satisfies RTRef as unknown as DOMImperativeFactory,
    []
  );

  useMeassure(onSize, ref);

  useLayoutEffect(() => {
    document.documentElement.style.fontSize = `${12 * props.scaleFactor}px`;
  }, [props.scaleFactor]);
  return (
    <div ref={ref} style={{ minHeight: 1 }}>
      <EditorContent editor={editor} />
    </div>
  );
}

function useMeassure(
  onSize: ((height: number) => Promise<void>) | undefined,
  ref: RefObject<HTMLDivElement | null>
) {
  const prevHeight = useRef(0);
  const measure = useCallback(() => {
    if (!onSize) {
      return;
    }
    const height =
      ref.current?.getBoundingClientRect().height ??
      ref.current?.scrollHeight ??
      1;
    if (Math.abs(height - prevHeight.current) > 1) {
      // ignore tiny diffs
      prevHeight.current = height;
      onSize?.(height);
    }
  }, [onSize, ref]);

  useLayoutEffect(() => {
    if (!onSize) {
      return;
    }

    let cancelled = false;

    // Function to measure and notify native side

    const elem = ref.current ?? document.documentElement;
    if ("ResizeObserver" in window) {
      const ro = new ResizeObserver((entries) => {
        measure();
      });
      ro.observe(elem);
      // also run once after images & fonts settle
      document.fonts?.ready?.then(measure);
      Array.from(document.images).forEach((img) => {
        if (!img.complete)
          img.addEventListener(
            "load",
            () => {
              if (!cancelled) {
                measure();
              }
            },
            { once: true }
          );
      });
      measure();
      return () => {
        cancelled = true;
        ro.disconnect();
      };
    }

    const mo = new MutationObserver(() => measure());
    mo.observe(elem, {
      childList: true,
      subtree: true,
      attributes: true,
      characterData: true,
    });

    // cleanup
    return () => {
      cancelled = true;
      mo.disconnect();
    };
  }, [onSize, measure, ref]);
}
